import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookContext } from "../contexts/BookContext";
import { AnecdotesModalContext } from "../contexts/AnecdotesModalContext";
import { Locale } from "../types/models/Language";
import { GeneratedChapterStatusBadge } from "./Badges/GeneratedChapterStatusBadge";
import { GeneratedChapterTableActionsButton } from "./Buttons/GeneratedChapterTableActionsButton";

export const GeneratedChaptersTable = () => {
  // Hooks ===============================================
  const { t } = useTranslation();
  const local = (localStorage.getItem("i18nextLng") || "en") as Locale;

  // Context =============================================
  const bookContext = useContext(BookContext);
  const generatedChapters = bookContext.generated_chapters;
  const { openModal } = useContext(AnecdotesModalContext);

  // States ===============================================
  const [renderKey, setRenderKey] = useState(200);

  useEffect(() => {
    setRenderKey((prevKey) => prevKey + 1);
  }, [generatedChapters]);

  const handleOpenAnecdotesModal = (
    chapterNumber: number,
    chapterTitle: string,
    generatedChapterId?: number
  ) => {
    // Open modal with chapterNumber (always present) and generatedChapter.id (optional)
    openModal(chapterNumber, chapterTitle, generatedChapterId);
  };

  return (
    <div
      key={renderKey}
      className="bg-white flex flex-col justify-between shadow-md rounded overflow-hidden"
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="w-full sm:w-4/12 py-3.5 pl-4 text-left text-xs sm:text-sm font-nunito font-semibold text-slate-50 sm:pl-6"
              >
                {t("generatedChapter.title").toUpperCase()}
              </th>
              <th
                scope="col"
                className="hidden sm:table-cell w-5/12 px-3 py-3.5 text-left text-sm font-nunito font-semibold text-slate-50"
              >
                {t("generatedChapter.wordCount").toUpperCase()}
              </th>
              <th
                scope="col"
                className="hidden md:table-cell w-1/12 px-3 py-3.5 text-left text-sm font-nunito font-semibold text-slate-50"
              >
                {t("generatedChapter.status").toUpperCase()}
              </th>
              <th
                scope="col"
                className="w-auto sm:w-2/12 relative py-3.5 pl-3 pr-4 sm:pr-6"
              >
                <span className="sr-only">{t("dashboard.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {/* ...existing code... */}
            {generatedChapters
              .sort((a, b) => a.chapter_id - b.chapter_id)
              .map((generatedChapter) => {
                const chapter = bookContext.chapters.find(
                  (chapter) => chapter.id === generatedChapter.chapter_id
                );
                if (!chapter) return null;
                return (
                  <tr key={generatedChapter.id}>
                    <td className="py-4 pl-4 pr-3 text-xs sm:text-sm font-nunito font-normal text-slate-700 sm:pl-6">
                      <div className="block sm:hidden mb-2">
                        <div className="flex flex-col space-y-1">
                          <span className="font-medium">
                            {generatedChapter.title}
                          </span>
                          {generatedChapter?.word_count &&
                            generatedChapter?.word_count > 0 && (
                              <span className="text-xs text-slate-500">
                                {generatedChapter.word_count}{" "}
                                {t("generatedChapter.words")}
                              </span>
                            )}
                          <div className="md:hidden">
                            <GeneratedChapterStatusBadge
                              completed={generatedChapter.completed}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="hidden sm:block">
                        {generatedChapter.title}
                      </div>
                    </td>
                    <td className="hidden sm:table-cell text-sm py-4 pl-4 pr-3 font-nunito font-normal text-slate-700 sm:pl-6">
                      <div className="flex flex-row items-center gap-x-3">
                        {generatedChapter?.word_count &&
                          generatedChapter?.word_count > 0 &&
                          `${generatedChapter.word_count} ${t(
                            "generatedChapter.words"
                          )}`}
                      </div>
                    </td>
                    <td className="hidden md:table-cell px-3 py-4 text-sm text-slate-500">
                      <GeneratedChapterStatusBadge
                        completed={generatedChapter.completed}
                      />
                    </td>
                    <td className="relative py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      <GeneratedChapterTableActionsButton
                        chapterNumber={chapter.number}
                        onAnecdotesClick={() => handleOpenAnecdotesModal(chapter.number, chapter.content[local], generatedChapter.id)}
                      />
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
